<script lang="ts">
  import { i18n } from '../i18n/index.svelte';
  import {
    ACHIEVEMENTS,
    ACHIEVEMENT_CATEGORIES,
    ACHIEVEMENT_TOTAL,
    type AchievementCategory,
    type AchievementState,
  } from '@2k48/shared';
  import { animateTabPanel } from '../../game/motion';
  import TopBar from '../components/TopBar.svelte';
  import SegmentedBar from '../components/SegmentedBar.svelte';
  import AchievementCard from '../components/AchievementCard.svelte';
  import PixelTabs from '../components/PixelTabs.svelte';
  import PixelPanel from '../components/PixelPanel.svelte';
  import type { PixelIconName } from '../components/PixelIcon.svelte';
  import { session } from '../stores/session.svelte';
  import { router } from '../stores/router.svelte';
  import { api } from '../../net/client';
  import { longDate, percentOf } from '../format';

  /**
   * The catalogue is in the bundle; the server only sends back what this player
   * has actually touched. So an achievement nobody has started still renders,
   * with its real name, goal and an empty bar — which is the whole point of a
   * list you are supposed to work through.
   */

  interface AchievementsResponse {
    entries: AchievementState[];
    total: number;
    unlocked: number;
  }

  const t = $derived(i18n.t);

  const FILTERS = $derived<{ id: AchievementCategory | 'todos'; label: string }[]>([
    { id: 'todos', label: t.achievements.all },
    ...ACHIEVEMENT_CATEGORIES.map((category) => ({
      id: category,
      label: t.achievements.category[category],
    })),
  ]);

  /**
   * The words for an achievement, resolved by id.
   *
   * `shared` owns which counter unlocks what; the name and the description are
   * copy and live in the catalogue. The fallback should be unreachable — a test
   * asserts both directions of id parity — and exists so that an id arriving
   * from a newer server renders as something rather than as a blank row.
   */
  function wordsFor(id: string): { name: string; description: string } {
    const items = t.achievements.items as Record<string, { name: string; description: string }>;
    return items[id] ?? { name: id.toUpperCase(), description: '' };
  }

  let filter = $state<AchievementCategory | 'todos'>('todos');
  let states = $state<Map<string, AchievementState>>(new Map());
  let loading = $state(true);
  let failed = $state(false);
  const playerId = $derived(router.current.params.player || session.profile?.id);

  $effect(() => {
    const player = playerId;
    if (!player) {
      loading = false;
      return;
    }

    let cancelled = false;
    loading = true;

    void api<AchievementsResponse>(`/achievements/${player}`)
      .then((response) => {
        if (cancelled) return;
        states = new Map(response.entries.map((entry) => [entry.id, entry]));
        failed = false;
      })
      .catch(() => {
        if (cancelled) return;
        states = new Map();
        failed = true;
      })
      .finally(() => {
        if (!cancelled) loading = false;
      });

    return () => {
      cancelled = true;
    };
  });

  const unlockedCount = $derived(
    [...states.values()].filter((state) => state.unlockedAt !== null).length,
  );

  const visible = $derived(
    ACHIEVEMENTS.filter((achievement) => filter === 'todos' || achievement.category === filter),
  );

  function stateOf(id: string): AchievementState | undefined {
    return states.get(id);
  }

  /**
   * A secret achievement gives nothing away until it is earned: no name, no
   * goal, no progress bar creeping towards something unnamed.
   */
  function titleFor(name: string, secret: boolean, unlocked: boolean): string {
    return secret && !unlocked ? i18n.t.achievements.secret : name;
  }

  function descriptionFor(
    description: string,
    secret: boolean,
    unlocked: boolean,
    state: AchievementState | undefined,
  ): string {
    if (secret && !unlocked) return i18n.t.achievements.secretHint;
    if (unlocked && state?.unlockedAt) return `${description}  ·  ${longDate(state.unlockedAt)}`;
    return description;
  }
</script>

<div class="view achievements">
  <TopBar title={t.achievements.title} />

  <div class="view__body">
    <PixelPanel variant="accent" class="achievements__summary">
      <span class="achievements__count">
        <span class="lbl">{t.achievements.unlocked}</span>
        <span class="achievements__score">
          {unlockedCount}<span class="achievements__total">/{ACHIEVEMENT_TOTAL}</span>
        </span>
      </span>
      <SegmentedBar value={unlockedCount} max={ACHIEVEMENT_TOTAL} />
    </PixelPanel>

    <PixelTabs
      class="achievements__tabs"
      label={t.achievements.ariaCategory}
      value={filter}
      options={FILTERS}
      onchange={(value) => (filter = value as AchievementCategory | 'todos')}
    />

    {#if loading}
      <p class="view__placeholder">{t.common.loading}</p>
    {:else}
      {#if failed}
        <!-- The catalogue still renders offline; only the progress is missing,
             so the screen degrades to "nothing unlocked yet" rather than to an
             error page. -->
        <p class="achievements__offline tiny">{t.achievements.offline}</p>
      {/if}

      {#key filter}
        <div class="achievements__list" use:animateTabPanel>
          {#each visible as achievement (achievement.id)}
            {@const state = stateOf(achievement.id)}
            {@const unlocked = state?.unlockedAt != null}
            <AchievementCard
              icon={achievement.icon as PixelIconName}
              title={titleFor(wordsFor(achievement.id).name, achievement.secret === true, unlocked)}
              description={descriptionFor(
                wordsFor(achievement.id).description,
                achievement.secret === true,
                unlocked,
                state,
              )}
              rarity={achievement.secret === true && !unlocked ? undefined : achievement.rarity}
              {unlocked}
              progress={achievement.secret === true && !unlocked
                ? 0
                : percentOf(state?.progress ?? 0, achievement.goal)}
            />
          {/each}
        </div>
      {/key}
    {/if}
  </div>
</div>
